// src/pages/Purchase/Purchase Entry/hooks/usePurchaseBarcodesPrint.js
//
// ✅ OPTIMISED — single API call only (purchaseRecordnWithDetailsApi).
// item_barcode now comes directly from details[] — no extra fetchProductsList calls.
// ✅ TWO SIZES — "standard" (48×30 mm, 4×9 per page) and "small" (24×15 mm, 8×18 per page).

import { useState } from "react";
import { purchaseRecordnWithDetailsApi } from "../../../../../apiservice/purchase";

// ─── Label size presets ───────────────────────────────────────────────────────
const LABEL_SIZES = {
  standard: { labelsPerRow: 4, labelsPerCol: 9,  widthMm: 48, heightMm: 30 },
  small:    { labelsPerRow: 8, labelsPerCol: 18, widthMm: 24, heightMm: 15 },
};

// ─── HTML builder ─────────────────────────────────────────────────────────────
function buildBarcodeHtml(labelItems, voucherNo = "", size = "standard") {
  const { labelsPerRow, labelsPerCol, widthMm, heightMm } =
    LABEL_SIZES[size] || LABEL_SIZES.standard;

  const nameFontSize  = size === "small" ? "5px" : "7px";
  const valueFontSize = size === "small" ? "5px" : "7px";
  const barcodeHeight = size === "small" ? 20    : 40;
  const barcodeWidth  = size === "small" ? 1.2   : 1.5;

  // Expand each item by its quantity into individual label slots
  const labels = [];
  for (const item of labelItems) {
    const qty = Math.max(1, Math.round(Number(item.qty) || 1));
    for (let i = 0; i < qty; i++) {
      labels.push({ name: item.name, barcodeValue: item.barcodeValue });
    }
  }

  if (labels.length === 0) {
    return `<!DOCTYPE html><html><body style="font-family:sans-serif;padding:40px;">
      <h3 style="color:#d32f2f;">No barcodes found</h3>
      <p>None of the items in this purchase have an <strong>item_barcode</strong> assigned.<br/>
      Go to <strong>Inventory → Products</strong>, open each product and fill in the Barcode field.</p>
    </body></html>`;
  }

  const labelSnippets = labels
    .map(
      (lbl, idx) => `
      <div class="label">
        <div class="product-name" title="${escapeHtml(lbl.name)}">${escapeHtml(lbl.name)}</div>
        <svg id="bc_${idx}"></svg>
        <div class="barcode-value">${escapeHtml(lbl.barcodeValue)}</div>
      </div>`
    )
    .join("\n");

  const jsBarcodeCalls = labels
    .map(
      (lbl, idx) => `
      try {
        JsBarcode("#bc_${idx}", ${JSON.stringify(lbl.barcodeValue)}, {
          format: "CODE128",
          width: ${barcodeWidth},
          height: ${barcodeHeight},
          displayValue: false,
          margin: 0
        });
      } catch(e) {
        var el = document.getElementById("bc_${idx}");
        if (el) el.outerHTML = '<div style="font-size:8px;color:#f00;text-align:center;">Invalid</div>';
      }`
    )
    .join("\n");

  const totalPages = Math.ceil(labels.length / (labelsPerRow * labelsPerCol));
  const sizeLabel  = size === "small" ? "Small (24×15 mm)" : "Standard (48×30 mm)";
  const titleText  = voucherNo
    ? `Barcodes — ${voucherNo} &nbsp;(${labels.length} label${labels.length !== 1 ? "s" : ""}, ~${totalPages} page${totalPages !== 1 ? "s" : ""}, ${sizeLabel})`
    : `Barcodes &nbsp;(${labels.length} label${labels.length !== 1 ? "s" : ""}, ~${totalPages} page${totalPages !== 1 ? "s" : ""}, ${sizeLabel})`;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <title>Purchase Barcodes${voucherNo ? " — " + voucherNo : ""}</title>
  <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"></script>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; background: #fff; }

    .toolbar {
      position: sticky; top: 0; z-index: 10;
      background: #1677ff; color: #fff;
      padding: 8px 16px;
      display: flex; gap: 12px; align-items: center;
      font-size: 14px;
    }
    .toolbar-title { flex: 1; font-weight: 700; }
    .btn {
      padding: 6px 18px; border-radius: 4px;
      cursor: pointer; font-size: 13px; font-weight: 600; border: none;
    }
    .btn-print { background: #fff; color: #1677ff; }
    .btn-close  { background: transparent; color: #fff; border: 1px solid rgba(255,255,255,.5); }

    .grid {
      display: grid;
      grid-template-columns: repeat(${labelsPerRow}, ${widthMm}mm);
      grid-auto-rows: ${heightMm}mm;
      gap: 1mm;
      padding: 6mm 10mm;
      width: 210mm;
    }

    .label {
      width:  ${widthMm}mm;
      height: ${heightMm}mm;
      border: 0.4px solid #ccc;
      border-radius: 2px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 1.5mm 1mm;
      overflow: hidden;
      page-break-inside: avoid;
    }

    .label:nth-child(${labelsPerRow * labelsPerCol}n) {
      page-break-after: always;
    }

    .product-name {
      font-size: ${nameFontSize};
      font-weight: 700;
      text-align: center;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      width: 100%;
      margin-bottom: 1px;
    }

    .label svg { max-width: 100%; height: auto; }

    .barcode-value {
      font-size: ${valueFontSize};
      margin-top: 1px;
      letter-spacing: 0.5px;
      text-align: center;
    }

    @media print {
      @page { size: A4 portrait; margin: 0; }
      body { margin: 0; }
      .toolbar { display: none !important; }
      .grid { padding: 6mm 10mm; }
    }
  </style>
</head>
<body>

  <div class="toolbar">
    <span class="toolbar-title">${titleText}</span>
    <button class="btn btn-print" onclick="window.print()">🖨 Print / Save PDF</button>
    <button class="btn btn-close"  onclick="window.close()">✕ Close</button>
  </div>

  <div class="grid">
    ${labelSnippets}
  </div>

  <script>
    window.addEventListener("load", function () {
      ${jsBarcodeCalls}
    });
  </script>
</body>
</html>`;
}

function escapeHtml(str) {
  return String(str || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function openPrintWindow(html) {
  const win = window.open("", "_blank", "width=960,height=720");
  if (!win) {
    alert("Popup blocked — please allow popups for this site and try again.");
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function usePurchaseBarcodesPrint() {
  const [loading, setLoading] = useState(false);

  /**
   * printBarcodesForPurchase
   * ─────────────────────────
   * 1 API call total — item_barcode comes straight from details[].
   * No fetchProductsList, no parallel calls, no Redux dependency.
   *
   * @param {string}   purId      pur_id of the saved purchase
   * @param {string}   voucherNo  shown in print toolbar title
   * @param {Function} onError    optional (errMessage: string) => void
   * @param {string}   size       "standard" (48×30 mm) | "small" (24×15 mm)
   */
  const printBarcodesForPurchase = async (
    purId,
    voucherNo = "",
    onError,
    size = "standard"
  ) => {
    if (!purId) return;
    setLoading(true);

    try {
      // ── Single API call ───────────────────────────────────────────────────
      const res = await purchaseRecordnWithDetailsApi({
        pur_id: purId,
        from: "voucher",
      });

      if (!res?.data?.length) throw new Error("Purchase record not found");

      const details = res.data[0]?.details || [];
      if (!details.length) throw new Error("This purchase has no items");

      // ── Build label list directly from details[] ──────────────────────────
      const labelItems = details
        .map((d) => ({
          name:         d.item_name    || "",
          barcodeValue: d.item_barcode || "",   // ✅ comes from backend directly
          qty:          Number(d.item_qty) || 1,
        }))
        .filter((l) => l.barcodeValue); // skip items with no barcode

      openPrintWindow(buildBarcodeHtml(labelItems, voucherNo, size));
    } catch (err) {
      console.error("[usePurchaseBarcodesPrint]", err);
      const msg = err?.message || "Failed to load barcode data";
      if (onError) onError(msg);
      else alert(msg);
    } finally {
      setLoading(false);
    }
  };

  return { printBarcodesForPurchase, loading };
}